import { useEffect, useState } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import Card from '../../../Card/Card';
import CheckboxGroup from '../../../FormElements/CheckboxGroup/CheckboxGroup';
import TabActionButtons from '../../../FormElements/TabActionButtons/TabActionButtons';
import ToggleSwitch from '../../../FormElements/ToggleSwitch/ToggleSwitch';
import { countriesData } from "../../../utils/helpers";
import styles from './TabDropdown.module.css';

const MiscSettingsSettings = ({ onSave, onReset, settingsData, onSettingsChange, showToast }) => {
  const [settings, setSettings] = useState({
    // Editor Settings
    hvnly_EnabledGutenbergEditor: false,
    
    // Social Share
    hvnly_EnabledSocialShare: true,
    hvnly_enableSocialShare: ['facebook', 'twitter', 'linkedin', 'pinterest', 'email', 'whatsapp', 'telegram', 'reddit', 'tumblr', 'viber', 'line', 'copy_link'],
    
    // Location Defaults
    hvnly_defaultCity: 'Birmingham',
    hvnly_defaultState: 'Scotland',
    hvnly_countryType: 'GB',
    hvnly_defaultZipCode: '',
    hvnly_defaultNeighborhood: '',
  });

  // Load settings from props when available
  useEffect(() => {
    if (settingsData && typeof settingsData === 'object' && Object.keys(settingsData).length > 0) {
      const miscFields = {
        hvnly_EnabledGutenbergEditor: settingsData.hvnly_EnabledGutenbergEditor !== undefined ? settingsData.hvnly_EnabledGutenbergEditor : settings.hvnly_EnabledGutenbergEditor,
        hvnly_EnabledSocialShare: settingsData.hvnly_EnabledSocialShare !== undefined ? settingsData.hvnly_EnabledSocialShare : settings.hvnly_EnabledSocialShare,
        hvnly_enableSocialShare: settingsData.hvnly_enableSocialShare !== undefined ? settingsData.hvnly_enableSocialShare : settings.hvnly_enableSocialShare,
        hvnly_defaultCity: settingsData.hvnly_defaultCity !== undefined ? settingsData.hvnly_defaultCity : settings.hvnly_defaultCity,
        hvnly_defaultState: settingsData.hvnly_defaultState !== undefined ? settingsData.hvnly_defaultState : settings.hvnly_defaultState,
        hvnly_countryType: settingsData.hvnly_countryType !== undefined ? settingsData.hvnly_countryType : settings.hvnly_countryType,
        hvnly_defaultZipCode: settingsData.hvnly_defaultZipCode !== undefined ? settingsData.hvnly_defaultZipCode : settings.hvnly_defaultZipCode,
        hvnly_defaultNeighborhood: settingsData.hvnly_defaultNeighborhood !== undefined ? settingsData.hvnly_defaultNeighborhood : settings.hvnly_defaultNeighborhood,
      };
      setSettings(prev => ({ ...prev, ...miscFields }));
    }
  }, [settingsData]);

  const handleInputChange = (field, value) => {
    const newSettings = { ...settings, [field]: value };
    setSettings(newSettings);
    if (onSettingsChange) {
      onSettingsChange(newSettings);
    }
  };

  const handleSave = async () => {
    if (onSave) {
      await onSave(settings);
      return true;
    }
    return false;
  };

  const handleReset = async () => {
    const defaultSettings = {
      hvnly_EnabledGutenbergEditor: false,
      hvnly_EnabledSocialShare: true,
      hvnly_enableSocialShare: ['facebook', 'twitter', 'linkedin', 'pinterest', 'email', 'whatsapp'],
      hvnly_defaultCity: 'Birmingham',
      hvnly_defaultState: 'Scotland',
      hvnly_countryType: 'GB',
      hvnly_defaultZipCode: '',
      hvnly_defaultNeighborhood: '',
    };
    setSettings(defaultSettings);
    if (onSettingsChange) {
      onSettingsChange(defaultSettings);
    }
    if (onReset) {
      await onReset(defaultSettings);
      return true;
    }
    return false;
  };

  const countryOptions = countriesData;

  return (
    <div className={`${styles['hvnly-settings-api__content']} ${styles['hvnly-settings-api__content--active']}`} id="misc-settings">
      <h2>{__('Misc Settings', 'havenlytics')}</h2>

      {/* Editor Settings Card */}
      <Card title={__("Editor Settings", "havenlytics")} icon="fas fa-edit" collapsible>
        <ToggleSwitch
          id="hvnly_EnabledGutenbergEditor"
          checked={settings.hvnly_EnabledGutenbergEditor}
          onChange={(checked) => handleInputChange('hvnly_EnabledGutenbergEditor', checked)}
          label={__("Enable Gutenberg Editor for Properties", "havenlytics")}
        />
      </Card>

      {/* Social Share Card */}
      <Card title={__("Social Share", "havenlytics")} icon="fas fa-share-alt" collapsible>
        <ToggleSwitch
          id="hvnly_EnabledSocialShare"
          checked={settings.hvnly_EnabledSocialShare}
          onChange={(checked) => handleInputChange('hvnly_EnabledSocialShare', checked)}
          label={__("Enable Social Share Buttons", "havenlytics")}
        />
        
        <CheckboxGroup
          label={__("Enable Social Share Platforms", "havenlytics")}
          options={[
            { value: 'facebook', label: __('Facebook', 'havenlytics') },
            { value: 'twitter', label: __('Twitter/X', 'havenlytics') },
            { value: 'linkedin', label: __('LinkedIn', 'havenlytics') },
            { value: 'pinterest', label: __('Pinterest', 'havenlytics') },
            { value: 'email', label: __('Email', 'havenlytics') },
            { value: 'whatsapp', label: __('WhatsApp', 'havenlytics') },
            { value: 'telegram', label: __('Telegram', 'havenlytics') },
            { value: 'reddit', label: __('Reddit', 'havenlytics') },
            { value: 'tumblr', label: __('Tumblr', 'havenlytics') },
            { value: 'viber', label: __('Viber', 'havenlytics') },
            { value: 'line', label: __('Line', 'havenlytics') },
            { value: 'copy_link', label: __('Copy Link', 'havenlytics') },
          ]}
          selectedValues={settings.hvnly_enableSocialShare}
          onChange={values => handleInputChange('hvnly_enableSocialShare', values)}
        />
      </Card>

      {/* Location Settings Card */}
      {/* <Card title={__("Default Location Settings", "havenlytics")} icon="fas fa-map-marker-alt" collapsible>
        <SelectDropDown
          label={__("Default Country", "havenlytics")}
          id="hvnly_countryType"
          value={settings.hvnly_countryType}
          options={countryOptions}
          onChange={(value) => handleInputChange('hvnly_countryType', value)}
        />
        <InputGroup
          label={__("Default City", "havenlytics")}
          id="hvnly_defaultCity"
          type="text"
          value={settings.hvnly_defaultCity}
          onChange={(value) => handleInputChange("hvnly_defaultCity", value)}
        />
        <InputGroup
          label={__("Default State/Province", "havenlytics")}
          id="hvnly_defaultState"
          type="text"
          value={settings.hvnly_defaultState}
          onChange={(value) => handleInputChange("hvnly_defaultState", value)}
        />
        <InputGroup
          label={__("Default Zip/Postal Code", "havenlytics")}
          id="hvnly_defaultZipCode"
          type="text"
          value={settings.hvnly_defaultZipCode}
          onChange={(value) => handleInputChange("hvnly_defaultZipCode", value)}
        />
        <InputGroup
          label={__("Default Neighborhood", "havenlytics")}
          id="hvnly_defaultNeighborhood"
          type="text"
          value={settings.hvnly_defaultNeighborhood}
          onChange={(value) => handleInputChange("hvnly_defaultNeighborhood", value)}
        />
      </Card> */}

      <TabActionButtons
        onSave={handleSave}
        onReset={handleReset}
        showToast={showToast}
        saveLabel={__("Save Misc Settings", "havenlytics")}
        resetLabel={__("Reset Changes", "havenlytics")}
      />
    </div>
  );
};

export default MiscSettingsSettings;